import React, {useCallback} from 'react';
import PopupTable, {PopupTableRow} from './PopupTable';
import fetchJSON from '../../../utils/fetchJSON';
import {isString} from 'lodash';
import useGetSyncPromise from '../../../utils/react/useGetSyncPromise';
import {formatElevation, formatDistance, formatTimeWithHours} from '../../../utils/formatters';
import {CircularProgress} from '@material-ui/core';
import PopupCloseButton from './PopupCloseButton';
import Typography from '@material-ui/core/Typography';
import theme from '../../../theme';
import fetchUser from '../../../utils/gaia/fetchUser';

interface OsmPopupOptions {
    osmId: number;
    title?: string;
    onPopupChange: (popup: MapPopup) => void;
}

const fetchOsmDetails = (id: number): Promise<OsmDetails> => {
    return fetchJSON.memoized(`/api/osm/id/${id}/`);
};

export default function OsmPopup(options: OsmPopupOptions) {
    const {osmId, onPopupChange} = options;
    const maxWidth = 250;
    const getSyncPromise = useGetSyncPromise();
    const onCloseClick = useCallback(() => onPopupChange(undefined), [onPopupChange]);
    const [user] = getSyncPromise(fetchUser());

    const [OsmDetails, error] = getSyncPromise(fetchOsmDetails(osmId));

    if (!OsmDetails) {
        return <div>
            <PopupCloseButton onClick={onCloseClick} />

            <div style={{display: 'flex', flexDirection: 'column', maxWidth}}>
                {options.title && <Typography variant='h6'>{options.title}</Typography>}

                {error ? null : <CircularProgress
                    style={{marginTop: theme.spacing(1), alignSelf: 'center'}}
                    thickness={7}
                    disableShrink
                />}
            </div>
        </div>;

    } else {
        const {
            title,
            formatted_tags,
            websites,
            distance,
            description,
            elevation_gain,
            estimated_time,
            hikeui_permalink,
        } = OsmDetails;

        return <div style={{maxWidth, maxHeight: 400, overflow: 'scroll'}}>
            <PopupCloseButton onClick={onCloseClick} />

            <Typography variant='h6' style={{marginBottom: theme.spacing(0.5)}}>{title}</Typography>

            <Typography variant='body2'>{formatDescription(description, user)}</Typography>

            <PopupTable style={{marginTop: theme.spacing(1)}}>

                {formatted_tags && formatted_tags.map(({key, title: label, info}) =>
                    <PopupTableRow
                        key={key}
                        label={label}
                        dangerouslySetInnerHTML={{__html: info}}
                    />
                )}

                {websites && websites.length > 0 && <PopupTableRow label='Websites'>
                    {websites.map(({link, text}, index) => <React.Fragment key={text}>
                        {index !== 0 ? ', ' : ''}
                        <a target='_new' href={link}>{text}</a>
                    </React.Fragment>)}
                </PopupTableRow>}

                {distance && <PopupTableRow label='Distance'>
                    {formatDistance(distance, user)}
                </PopupTableRow>}

                {elevation_gain && <PopupTableRow label='Elev Gain'>
                    {formatElevation(elevation_gain, user)}
                </PopupTableRow>}

                {estimated_time && <PopupTableRow label='Est Time'>
                    {formatTimeWithHours(estimated_time)}
                </PopupTableRow>}

                {hikeui_permalink && <PopupTableRow label='More Info'>
                    <a rel="noopener noreferrer" href={hikeui_permalink} target='_blank'>
                        Nearby Hikes
                    </a>
                </PopupTableRow>}
            </PopupTable>

        </div>;
    }

}

function formatDescription(description: OsmDetails['description'], user: User | undefined): string {
    if (!description) {
        return '';
    } else if (isString(description)) {
        return description;
    } else if (description.distance_in_meters !== undefined) {
        const distance = formatDistance(description.distance_in_meters, user);
        return description.template.replace('%s', distance);
    } else if (description.elevation_in_meters !== undefined) {
        const elevation = formatElevation(description.elevation_in_meters, user);
        return description.template.replace('%s', elevation);
    } else {
        return '';
    }
}

interface OsmDetails {
    id: number;
    osm_id: number;
    distance?: number;
    elevation_gain?: number;
    estimated_time?: number;
    hikeui_permalink: string;
    type: string;
    title: string;
    formatted_tags: {key: string; title: string; info: string}[];
    description?: (
        string |
        {template: string; elevation_in_meters: number; distance_in_meters: void} |
        {template: string; distance_in_meters: number; elevation_in_meters: void}
    );
    websites?: {
        link: string;
        text: string;
    }[];
}
